import {
  NOTIFICATION_POSITION,
  NOTIFICATION_STATUS,
  type Utilities,
  type Notification,
} from 'core/domains';
import { put, select } from 'core/middlewares/utilities';
import { app, cart, user, utilities } from 'core/use-cases';
import { formatString } from 'core/utilities';
import { v4 as uuidv4 } from 'uuid';

import { messages } from './event-messages';
import { GetStatus } from '../const/notification-const';

import type { NotificationSuccess, NotificationTimeout } from './notification.d';
import type { RootState } from 'core/store';

export function* getNotificationPosition() {
  const { isCartActive, isPointsProgramActive }: RootState['app'] = yield select(
    (state: RootState) => state.app,
  );

  if (isCartActive || isPointsProgramActive) {
    return NOTIFICATION_POSITION.TOP_RIGHT;
  }

  return NOTIFICATION_POSITION.TOP_CENTER;
}

const getErrorMessage = (action: utilities.ActionFailure) => {
  if (action.error?.response?.userMessage?.length) {
    return action.error?.response?.userMessage;
  }

  return messages.failure[action.type as keyof typeof messages.failure]
    ? messages.failure[action.type as keyof typeof messages.failure]
    : action.error?.message;
};

/**
 * Middleware responsible for handle error messages
 *
 */
export function* error(
  action: utilities.ActionFailure,
  icon?: string,
  personalizedMessage?: string,
) {
  const message = personalizedMessage ?? getErrorMessage(action);

  const payload = (
    action.meta && 'payload' in action.meta ? action.meta?.payload : {}
  ) as Utilities.PlainObject;

  const eventLevel = action.error?.response?.eventLevel ?? '';

  yield put(
    app.actions.showNotification({
      id: uuidv4(),
      message: formatString(message, payload),
      position: yield getNotificationPosition(),
      showIcon:
        action.type === cart.actions.itemAddFetch.failure.type ||
        action.type === cart.actions.itemUpdateFetch.failure.type ||
        action.type === cart.actions.itemDeleteFetch.failure.type,
      status: !eventLevel
        ? NOTIFICATION_STATUS.ERROR
        : (GetStatus[eventLevel as keyof typeof GetStatus] as Notification['status']),
      icon,
    }),
  );
}
/**
 * Middleware responsible for handle success messages
 *
 */
export function* success(
  action: utilities.ActionSuccess<NotificationSuccess>,
  eventLevel?: string,
  message?: string,
  showIcon?: boolean,
  icon?: string,
) {
  const timeout = {
    [user.actions.userBuyIntentionFetch.success.type]: 10_000,
  } as NotificationTimeout;

  const notificationMessage = message ?? messages.success[action.type];

  const GetStatus: { [key: string]: string } = {
    alert: 'warning',
    critical: 'error',
  };

  yield put(
    app.actions.showNotification({
      id: uuidv4(),
      message: formatString(notificationMessage, action.meta?.payload),
      position: yield getNotificationPosition(),
      showIcon: showIcon ?? action.type === user.actions.userBuyIntentionFetch.success.type,
      status: !eventLevel
        ? NOTIFICATION_STATUS.SUCCESS
        : (GetStatus[eventLevel as keyof typeof GetStatus] as Notification['status']),
      timeout: timeout[action.type] || 3_000,
      icon,
    }),
  );
}
